import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper, IconButton, Drawer, Typography } from "@material-ui/core";
import chatBgPattern from "../../assets/chat-bg-pattern.png";
import SvgIcon from "@material-ui/core/SvgIcon";

const CloseXIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </SvgIcon>
);

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const drawerWidth = 420;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfd2d6" : "#0e1418",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `url(${chatBgPattern})`,
      backgroundRepeat: "repeat",
      opacity: theme.mode === 'light' ? 1 : 0.35,
      pointerEvents: "none",
      zIndex: 0,
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    zIndex: 1,
    border: "none !important",
    backgroundColor: "transparent !important",
    background: "transparent !important",
  },

  contactDrawer: {
    "& .MuiDrawer-paper": {
      ...theme.chatiaDS.dialogPaper,
      width: drawerWidth,
      maxWidth: "85vw",
      maxHeight: "none !important",
      height: "100%",
      backgroundColor: theme.mode === "light" ? "#ffffff !important" : "#121212 !important",
      backgroundImage: "none !important",
      borderRadius: "21px 0 0 12px !important",
      border: "none",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    },
  },
  contactDrawerHeader: {
    ...theme.chatiaDS.header,
  },
  contactDrawerTitle: {
    ...theme.chatiaDS.headerTitle,
  },
  contactDrawerClose: {
    backgroundColor: theme.chatiaDS.closeButton.backgroundColor,
    color: theme.chatiaDS.closeButton.color,
    transition: theme.chatiaDS.closeButton.transition,
  },
}));

const Ticket = ({ ticketIdOverride, embedded } = {}) => {
  const params = useParams();
  const ticketId = ticketIdOverride || params.ticketId;
  const history = useHistory();
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [messageInputHeight, setMessageInputHeight] = useState(0);
  const [mediaPreviewActive, setMediaPreviewActive] = useState(false);
  const { companyId } = user;

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setContact(data.contact);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          if (!embedded) history.push("/tickets");
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    if (!ticket || ticketId === "undefined") {
      return;
    }
    if (!socket || typeof socket.on !== "function") {
      return;
    }

    if (user.companyId) {
      const ticketIdentifier = ticket.uuid || ticketId;

      const onConnectTicket = () => {
        socket.emit("joinChatBox", ticketIdentifier);
      }

      // Join immediately se já estiver conectado
      socket.emit("joinChatBox", ticketIdentifier);

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticket?.id) {
          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          if (!embedded) history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {
        if (socket && typeof socket.off === "function") {
          socket.emit("joinChatBoxLeave", ticketIdentifier);
          socket.off("connect", onConnectTicket);
          socket.off(`company-${companyId}-ticket`, onCompanyTicket);
          socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
        }
      };
    }
  }, [ticketId, ticket, history, socket, user.companyId]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleMessageInputResize = useCallback((height) => {
    setMessageInputHeight(height);
  }, []);

  const renderMessagesList = () => {
    return (
      <div style={{ position: "relative", display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          contactId={ticket.contact?.id}
          bottomPadding={messageInputHeight}
        >
        </MessagesList>
        <div style={mediaPreviewActive
          ? { position: "absolute", inset: 0, zIndex: 10 }
          : { position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 10 }
        }>
          <MessageInput
            ticketId={ticket.uuid || ticketId}
            ticketStatus={ticket.status}
            ticketChannel={ticket.channel}
            droppedFiles={dragDropFiles}
            contactId={contact.id}
            onHeightChange={handleMessageInputResize}
            onMediaPreviewChange={setMediaPreviewActive}
          />
        </div>
      </div>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        elevation={0}
        square
        className={classes.mainWrapper}
      >
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
          <TicketActionButtons
            ticket={ticket}
          />
        </TicketHeader>

        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderMessagesList()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      {/* Contact Drawer - overlay style */}
      <Drawer
        className={classes.contactDrawer}
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
      >
        <div className={classes.contactDrawerHeader}>
          <Typography className={classes.contactDrawerTitle}>
            Detalhes do contato
          </Typography>
          <IconButton
            size="small"
            onClick={handleDrawerClose}
            className={classes.contactDrawerClose}
          >
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          ticket={ticket}
          embedded
        />
      </Drawer>
    </div>
  );
};

export default Ticket;
